import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Controller, useFormContext } from "react-hook-form";
import { useAction } from "../../../../hooks/useAction";
import { DEFAULT_BLOCK_CSS } from "../../../../constant/constant";
import { addPx, getRandomId, parseCssStringToObject } from "../../../../helper";
import { useApp } from "../../../../context/AppState";


// Comprehensive input type configurations
const INPUT_TYPE_CONFIG = {
  text: {
    placeholder: "Enter your text here",
    type: "text",
    validation: {
      pattern: null,
      minLength: null,
      maxLength: 255,
    },
    icon: "📝",
  },
  email: {
    placeholder: "Enter your email address",
    type: "email",
    validation: {
      pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      message: "Please enter a valid email address",
    },
    icon: "📧",
  },
  number: {
    placeholder: "Enter a number",
    type: "number",
    validation: {
      min: null,
      max: null,
      step: 1,
    },
    icon: "🔢",
  },
  tel: {
    placeholder: "Enter your phone number",
    type: "tel",
    validation: {
      pattern: /^[\+]?[1-9][\d]{0,15}$/,
      message: "Please enter a valid phone number",
    },
    icon: "📞",
  },
  url: {
    placeholder: "Enter website URL",
    type: "url",
    validation: {
      pattern: /^https?:\/\/.+/,
      message: "Please enter a valid URL starting with http:// or https://",
    },
    icon: "🔗",
  },
  password: {
    placeholder: "Enter your password",
    type: "password",
    validation: {
      minLength: 6,
      pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      message: "Password must be at least 6 characters with uppercase, lowercase, and number",
    },
    icon: "🔒",
  },
  textarea: {
    placeholder: "Write your message here...",
    type: "textarea",
    validation: {
      minLength: null,
      maxLength: 1000,
    },
    icon: "📄",
  },
  date: {
    placeholder: "Select a date",
    type: "date",
    validation: {
      min: null,
      max: null,
    },
    icon: "📅",
  },
  time: {
    placeholder: "Select time",
    type: "time",
    validation: {},
    icon: "⏰",
  },
  datetime: {
    placeholder: "Select date and time",
    type: "datetime-local",
    validation: {
      min: null,
      max: null,
    },
    icon: "📅⏰",
  },
  search: {
    placeholder: "Search...",
    type: "search",
    validation: {
      minLength: 2,
      maxLength: 100,
    },
    icon: "🔍",
  },
  color: {
    placeholder: "Choose a color",
    type: "color",
    validation: {},
    icon: "🎨",
  },
  file: {
    placeholder: "Choose a file",
    type: "file",
    validation: {
      accept: "*/*",
      maxSize: 5242880, // 5MB
    },
    icon: "📁",
  },
  range: {
    placeholder: "Select a value",
    type: "range",
    validation: {
      min: 0,
      max: 100,
      step: 1,
    },
    icon: "📊",
  },
  select: {
    placeholder: "Select an option",
    type: "select",
    validation: {},
    icon: "📋",
    label: "Dropdown Select",
    options: [
      { value: "", label: "Select an option" },
      { value: "option_1", label: "Option 1" },
      { value: "option_2", label: "Option 2" },
      { value: "option_3", label: "Option 3" },
      { value: "option_4", label: "Option 4" },
      { value: "option_5", label: "Option 5" },
    ],
  },
  checkbox: {
    placeholder: "",
    type: "checkbox",
    validation: {},
    icon: "☑️",
    label: "I agree to the terms and conditions",
  },
  radio: {
    placeholder: "",
    type: "radio",
    validation: {},
    icon: "🔘",
    label: "Select an option",
    options: [
      { value: "option_1", label: "Option 1" },
      { value: "option_2", label: "Option 2" },
      { value: "option_3", label: "Option 3" },
      { value: "option_4", label: "Option 4" },
    ],
  },
};

// Helper function to get default placeholder based on input type
const getDefaultPlaceholder = (inputType) => {
  return INPUT_TYPE_CONFIG[inputType]?.placeholder || "Enter your text here";
};

// Helper function to get input configuration
const getInputConfig = (inputType) => {
  return INPUT_TYPE_CONFIG[inputType] || INPUT_TYPE_CONFIG.text;
};

const DEFAULT_REQUIRED_MESSAGES = {
  text: "Please enter your text",
  email: "Please enter your email address",
  number: "Please enter a number",
  tel: "Please enter your phone number",
  url: "Please enter a website URL",
  password: "Please enter your password",
  textarea: "Please enter your message",
  date: "Please select a date",
  time: "Please select a time",
  datetime: "Please select a date and time",
  search: "Please enter a search term",
  color: "Please choose a color",
  file: "Please choose a file",
  range: "Please select a value",
  select: "Please select an option",
  checkbox: "Please confirm to continue",
  radio: "Please select an option",
};

const getDefaultRequiredMessage = (inputType, fieldName) =>
  DEFAULT_REQUIRED_MESSAGES[inputType] || `${fieldName} is required`;

const getFieldErrorMessage = (error, section) => {
  if (!error) return "";

  const inputType = section?.inputType || "text";
  const fieldName = section?.inputName ?? "text";

  if (error.type === "duplicate") {
    const duplicateMessage = String(
      section?.rules?.duplicateMessage ??
        section?.rules?.duplicateEmailMessage ??
        ""
    ).trim();
    return (
      duplicateMessage ||
      error.message ||
      "This value has already been used."
    );
  }

  if (error.type === "pattern") {
    const patternMessage = String(section?.rules?.pattern?.message ?? "").trim();
    return (
      patternMessage ||
      error.message ||
      getInputConfig(inputType)?.validation?.message ||
      "Invalid format"
    );
  }

  if (error.type === "required") {
    const requiredMessage = String(section?.rules?.requiredMessage ?? "").trim();
    return (
      requiredMessage ||
      error.message ||
      getDefaultRequiredMessage(inputType, fieldName)
    );
  }

  return error.message || "";
};

const buildInputValidationRules = (section) => {
  const inputType = section?.inputType || "text";
  const inputConfig = getInputConfig(inputType);
  const fieldName = section?.inputName ?? "text";
  const rules = {};

  if (section?.rules?.required) {
    const requiredMessage = String(section?.rules?.requiredMessage ?? "").trim();
    const resolvedRequiredMessage =
      requiredMessage || getDefaultRequiredMessage(inputType, fieldName);

    if (inputType === "checkbox") {
      rules.validate = (value) =>
        value === true ? true : resolvedRequiredMessage;
    } else {
      rules.required = resolvedRequiredMessage;
    }
  }

  const schemaPatternValue = section?.rules?.pattern?.value;
  if (schemaPatternValue) {
    rules.pattern = {
      value: new RegExp(schemaPatternValue),
      message:
        String(section?.rules?.pattern?.message ?? "").trim() ||
        inputConfig?.validation?.message ||
        "Invalid format",
    };
  } else if (inputConfig?.validation?.pattern) {
    rules.pattern = {
      value: inputConfig.validation.pattern,
      message: inputConfig.validation.message || "Invalid format",
    };
  }

  if (inputConfig?.validation?.minLength) {
    rules.minLength = {
      value: inputConfig.validation.minLength,
      message: `Minimum ${inputConfig.validation.minLength} characters required`,
    };
  }

  if (inputConfig?.validation?.maxLength) {
    rules.maxLength = {
      value: inputConfig.validation.maxLength,
      message: `Maximum ${inputConfig.validation.maxLength} characters allowed`,
    };
  }

  return rules;
};

InputBlock.propTypes = {
  section: PropTypes.object.isRequired,
};

function InputWithInlineError({ error, input, section }) {
  const errorRef = useRef(null);
  const [errorMarginTop, setErrorMarginTop] = useState(0);

  useLayoutEffect(() => {
    if (!error) {
      setErrorMarginTop(0);
      return;
    }

    const node = errorRef.current;
    if (!node) return;

    const syncMarginTop = () => {
      setErrorMarginTop(node.offsetHeight);
    };

    syncMarginTop();

    const observer = new ResizeObserver(syncMarginTop);
    observer.observe(node);
    return () => observer.disconnect();
  }, [error]);

  return (
    <>
      {error && (
        <div
          ref={errorRef}
          role="alert"
          style={{
            marginTop: `-${errorMarginTop}px`,
            fontSize: "12px",
            color: "#d41019",
            display: "flex",
            alignItems: "flex-start",
            gap: "4px",
            lineHeight: 1.35,
            width: "100%",
            pointerEvents: "none",
          }}
        >
          {getFieldErrorMessage(error, section)}
        </div>
      )}
      {input}
    </>
  );
}

InputWithInlineError.propTypes = {
  error: PropTypes.shape({
    message: PropTypes.string,
    type: PropTypes.string,
  }),
  input: PropTypes.node.isRequired,
  section: PropTypes.object.isRequired,
};

export default function InputBlock({ section }) {
  const { control, clearErrors } = useFormContext();
  const { isMobile, appState } = useApp();
  const { actionHandler } = useAction();
  const fieldName = section?.inputName ?? "text";

  const randomId = useMemo(() => getRandomId(), [section]);

  useEffect(() => {
    if (appState?.is_preview_mode) {
      clearErrors(fieldName);
    }
  }, [appState?.is_preview_mode, clearErrors, fieldName]);

  const blockCSS = {
    ...DEFAULT_BLOCK_CSS,
    border: `1px solid rgba(0, 0, 0, 0.23)`,
    padding: "10px",
    pointerEvents: "auto", // Ensure input fields are clickable
    position: "relative",
    // Removed high z-index that might cause issues
    ...section?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"],
    borderRadius: addPx(
      section?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"]
        ?.borderRadius
    ),
    ...parseCssStringToObject(
      section?.setting?.overrideCSS?.[isMobile ? "mobile" : "desktop"]
    ),
  };

  return (
    <Controller
      name={fieldName}
      control={control}
      rules={buildInputValidationRules(section)}
      render={({ field, fieldState }) => {
        const inputConfig = getInputConfig(section?.inputType);
        const inputType = section?.inputType || "text";
        const showFieldError =
          Boolean(fieldState?.error) && !appState?.is_preview_mode;
        
        const commonInputStyle = {
          ...blockCSS,
          backgroundColor: "#ffffff",
          border: "2px solid #e1e5e9",
          borderRadius: "8px",
          padding: "12px 16px",
          fontSize: "14px",
          color: "#333333",
          transition: "all 0.2s ease",
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
          // Ensure input fields are always clickable and on top
          position: "relative",
          zIndex: 10000,
          pointerEvents: appState?.is_preview_mode ? "none" : "auto",
          ...(showFieldError && {
            borderColor: "#d41019",
            boxShadow: "0 1px 3px rgba(210, 16, 25, 0.2)",
          }),
        };

        const handleFocus = (e) => {
          e.target.style.borderColor = "#5c439f";
          e.target.style.boxShadow = "0 0 0 3px rgba(92, 67, 159, 0.1)";
        };

        const handleBlur = (e) => {
          field.onBlur();
          e.target.style.borderColor = fieldState?.error ? "#d41019" : "#e1e5e9";
          e.target.style.boxShadow = fieldState?.error ? "0 1px 3px rgba(210, 16, 25, 0.2)" : "0 1px 3px rgba(0, 0, 0, 0.1)";
        };

        const handleChange = (e) => {
          const applyChange = () => {
            field.onChange(e);
            clearErrors(fieldName);
          };

          // In preview mode, directly handle the change without actionHandler
          if (appState?.is_preview_mode) {
            applyChange();
          } else {
            // In editor mode, use actionHandler
            actionHandler(applyChange);
          }
        };

        // Render different input types
        let fieldInput;
        switch (inputType) {
          case "textarea":
            fieldInput = (
              <textarea
                id={randomId}
                className="draggable-input-field"
                placeholder={section?.placeholder ?? inputConfig.placeholder}
                {...field}
                style={{
                  ...commonInputStyle,
                  resize: "vertical",
                  minHeight: "100px",
                  fontFamily: "inherit",
                  lineHeight: "1.5",
                }}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            );
            break;

          case "select": {
            const selectOptions = section?.options || inputConfig.options;
            fieldInput = (
              <div style={{ position: "relative" }}>
                <select
                  id={randomId}
                  className="draggable-input-field"
                  {...field}
                  style={{
                    ...commonInputStyle,
                    cursor: "pointer",
                    appearance: "none",
                    backgroundImage: "url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23007CB2%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22/%3E%3C/svg%3E')",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 8px center",
                    backgroundSize: "12px auto",
                    paddingRight: "32px",
                  }}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                >
                  {selectOptions.map((option, index) => (
                    <option key={index} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            );
            break;
          }

          case "checkbox":
            fieldInput = (
              <div style={{ 
                display: "flex", 
                alignItems: "flex-start", 
                gap: "12px",
                padding: "8px 12px",
                borderRadius: "6px",
                border: "1px solid #e0e0e0",
                backgroundColor: "transparent",
                transition: "all 0.2s ease",
                cursor: "pointer",
              }}
              onClick={() => {
                field.onChange(!field.value);
              }}
              >
                <input
                  id={randomId}
                  className="draggable-input-field"
                  type="checkbox"
                  {...field}
                  style={{
                    width: "20px",
                    height: "20px",
                    margin: "0",
                    marginTop: "2px",
                    cursor: "pointer",
                    accentColor: "#1976d2",
                  }}
                  onChange={handleChange}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
                <label 
                  htmlFor={randomId} 
                  style={{ 
                    fontSize: "14px", 
                    color: "#333333", 
                    cursor: "pointer",
                    lineHeight: "1.4",
                    flex: "1",
                  }}
                >
                  {section?.label || inputConfig.label}
                </label>
              </div>
            );
            break;

          case "radio": {
            const radioOptions = section?.options || inputConfig.options;
            fieldInput = (
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {radioOptions.map((option, index) => (
                  <div key={index} style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    gap: "12px",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: field.value === option.value ? "2px solid #1976d2" : "1px solid #e0e0e0",
                    backgroundColor: field.value === option.value ? "rgba(25, 118, 210, 0.04)" : "transparent",
                    transition: "all 0.2s ease",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    field.onChange(option.value);
                  }}
                  >
                    <input
                      id={`${randomId}-${index}`}
                      className="draggable-input-field"
                      type="radio"
                      value={option.value}
                      checked={field.value === option.value}
                      {...field}
                      style={{
                        width: "20px",
                        height: "20px",
                        margin: "0",
                        cursor: "pointer",
                        accentColor: "#1976d2",
                      }}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                    <label 
                      htmlFor={`${randomId}-${index}`} 
                      style={{ 
                        fontSize: "14px", 
                        color: "#333333", 
                        cursor: "pointer",
                        fontWeight: field.value === option.value ? "500" : "400",
                        flex: "1",
                      }}
                    >
                      {option.label}
                    </label>
                  </div>
                ))}
              </div>
            );
            break;
          }

          default:
            fieldInput = (
              <input
                id={randomId}
                className="draggable-input-field"
                type={inputConfig.type}
                placeholder={section?.placeholder ?? inputConfig.placeholder}
                min={inputConfig.validation?.min}
                max={inputConfig.validation?.max}
                step={inputConfig.validation?.step}
                {...field}
                style={commonInputStyle}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
              />
            );
            break;
        }
        return (
          <InputWithInlineError
            error={showFieldError ? fieldState?.error : undefined}
            input={fieldInput}
            section={section}
          />
        );
      }}
    />
  );
}
